import { useState } from 'react';
import { cn } from '~/utils/cn';
import { tw } from '~/utils/tw';
import { getTypeColorClass, type JsonViewerTheme } from './types';
import { highlightText, JSON_SCHEMA_TYPES } from './utils';

interface InlinePrimitiveValueProps {
  value: unknown;
  theme: { string: string; number: string; boolean: string; null: string; searchMatch: string };
  searchText: string;
}

export function InlinePrimitiveValue({ value, theme, searchText }: InlinePrimitiveValueProps) {
  const lowerSearch = searchText?.toLowerCase() || '';

  const renderWithHighlight = (text: string, colorClass: string, quote = false) => {
    const quoteSpan = quote ? <span className="group-data-[openapi-mode=schema]/jv:hidden">"</span> : null;

    if (!lowerSearch) {
      return (
        <span className={colorClass}>
          {quoteSpan}
          {text}
          {quoteSpan}
        </span>
      );
    }

    const lowerText = text.toLowerCase();
    const matchIndex = lowerText.indexOf(lowerSearch);

    if (matchIndex === -1) {
      return (
        <span className={colorClass}>
          {quoteSpan}
          {text}
          {quoteSpan}
        </span>
      );
    }

    const before = text.slice(0, matchIndex);
    const match = text.slice(matchIndex, matchIndex + lowerSearch.length);
    const after = text.slice(matchIndex + lowerSearch.length);

    return (
      <span className={colorClass}>
        {quoteSpan}
        {before}
        <span className={theme.searchMatch}>{match}</span>
        {after}
        {quoteSpan}
      </span>
    );
  };

  if (value === null) return <span className={theme.null}>null</span>;
  if (typeof value === 'boolean') return renderWithHighlight(String(value), theme.boolean);
  if (typeof value === 'number') return renderWithHighlight(String(value), theme.number);
  if (typeof value === 'string') return renderWithHighlight(value, theme.string, true);

  return <span>{String(value)}</span>;
}

interface PrimitiveValueProps {
  value: unknown;
  type: string;
  theme: Pick<JsonViewerTheme, 'string' | 'number' | 'boolean' | 'null' | 'schemaType' | 'structureType' | 'searchMatch'>;
  collapseStringsAfterLength: number;
  searchText: string;
  openapiMode?: 'spec' | 'schema';
}

export function PrimitiveValue({ value, type, theme, collapseStringsAfterLength, searchText, openapiMode }: PrimitiveValueProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const baseClass = tw('wrap-break-word whitespace-pre-line');

  switch (type) {
    case 'string': {
      const str = String(value);

      // Schema-mode type keywords render unquoted and in their type color.
      if (openapiMode === 'schema' && JSON_SCHEMA_TYPES.has(str)) {
        return <span className={cn(baseClass, theme.schemaType, getTypeColorClass(str, theme))}>{str}</span>;
      }

      const shouldTruncate = str.length > collapseStringsAfterLength;
      const displayValue = !isExpanded && shouldTruncate ? str.slice(0, collapseStringsAfterLength) : str;
      const isMatch = searchText && str.toLowerCase().includes(searchText.toLowerCase());

      return (
        // biome-ignore lint/a11y/useKeyWithClickEvents: developer-facing JSON viewer; expanding a truncated string is a visual mouse affordance.
        <span
          className={cn(baseClass, 'inline-block max-w-[600px] align-top', shouldTruncate && 'cursor-pointer')}
          onClick={shouldTruncate ? () => setIsExpanded(!isExpanded) : undefined}
          title={shouldTruncate ? (isExpanded ? 'Click to collapse' : 'Click to expand') : undefined}
        >
          <span className="group-data-[openapi-mode=schema]/jv:hidden">"</span>
          {isMatch ? highlightText(displayValue, searchText, theme.string, theme.searchMatch) : <span className={theme.string}>{displayValue}</span>}
          {!isExpanded && shouldTruncate && <span className="text-muted-foreground">…</span>}
          <span className="group-data-[openapi-mode=schema]/jv:hidden">"</span>
        </span>
      );
    }
    case 'number': {
      const numStr = String(value);
      const isMatch = searchText && numStr.includes(searchText);
      return (
        <span className={baseClass}>
          {isMatch ? highlightText(numStr, searchText, theme.number, theme.searchMatch) : <span className={theme.number}>{numStr}</span>}
        </span>
      );
    }
    case 'boolean': {
      const boolStr = String(value);
      const isMatch = searchText && boolStr.toLowerCase().includes(searchText.toLowerCase());
      return (
        <span className={baseClass}>
          {isMatch ? highlightText(boolStr, searchText, theme.boolean, theme.searchMatch) : <span className={theme.boolean}>{boolStr}</span>}
        </span>
      );
    }
    case 'null':
      return <span className={cn(baseClass, theme.null)}>null</span>;
    case 'undefined':
      return <span className={cn(baseClass, theme.null)}>undefined</span>;
    default:
      return <span className={baseClass}>{String(value)}</span>;
  }
}
